// The pure logic behind ReportView — filter state, filtering, totals, export payload.
//
// SEPARATE FROM THE COMPONENT ON PURPOSE. This is where every rule that must not vary per report
// lives, and it is testable without a browser. ReportView is thin JSX over these functions; a new
// report definition should require ZERO changes to either file.

import { sumColumn, anyEstimated, anyRecovered, anyEffective, anyNotMeaningful, rawValue, compareBy, decidedNote, totalLabel, PENDING, ESTIMATE_FOOTNOTE, RECOVERED_FOOTNOTE, EFFECTIVE_FOOTNOTE, BASELINE_FOOTNOTE, BASELINE_UNMARKED_FOOTNOTE, NOT_MEANINGFUL_FOOTNOTE, PROJECTED_FOOTNOTE } from './format';

// ⛔ THE "NONE" SENTINEL — the whole reason enumerated filtering is centralised here.
// The old PropertyExecutiveSummary page did `statuses.includes(r.status)`, and null is never in
// that list, so every row with a null value was silently dropped. That is not hypothetical: the year
// roll writes appeal_status as NULL (roll_state_parcels, `null::text -- appeal_status NOT carried`),
// so every rolled parcel-year is invisible in that report today. A blank is a value a user must be
// able to see and to exclude deliberately — never one the filter disappears on their behalf.
// The sentinel is NUL-prefixed so it can never collide with a real column value, and it is written
// as an ESCAPE, not a literal NUL byte -- a raw NUL in source survives neither code review nor every
// toolchain cleanly. Caught by a byte-level check: the first draft embedded the real character.
export const NONE = '\u0000none';
export const NONE_LABEL = '(None)';

const isBlank = (v) => v == null || v === '';
export const filterKeyOf = (v) => (isBlank(v) ? NONE : String(v));

// ── filter definitions ────────────────────────────────────────────────────────────────────────
// A definition declares filters as strings (shorthand) or objects:
//   'client'                                        -> the Clients pick — one or several (pushes into SQL)
//   'years'                                         -> multi-year + "All years" (pushes into SQL)
//   { key, type:'enum',   label, from?, options?, picker?, noun? } -> chips (or picker:'search', a searchable multi-
//                                                     select — Property), incl. (None); nothing selected = all
//   { key, type:'toggle', label, options:[{value,label}], default }  -> exclusive chips
//   { key, type:'search', label, keys:[...] }       -> free-text across the named row keys
//   { key, type:'note', label, placeholder?, lines?, showWhen? } -> free text kept WITH the report (saved, presets)
//   any filter may carry showWhen(state) -> shown only when it returns true
//   { key, type:'notices', label, default:[...] }   -> the Notices chips: Original, each active Notice Type, All
//                                                     (multi; the row builder reads it — it never filters rows here)
const SHORTHAND = {
  client: { key: 'clientId', type: 'client', label: 'Client' },
  years: { key: 'years', type: 'years', label: 'Years' },
};

export function normaliseFilters(definition) {
  return (definition?.filters || []).map((f) => (typeof f === 'string' ? SHORTHAND[f] || { key: f, type: 'enum', label: f } : f)).filter(Boolean);
}

// Options for one enum filter: whatever the definition declared, else every distinct value present
// in the rows. (None) is appended whenever any row is blank there — and only then, so a column
// with no blanks doesn't grow a chip that can never match anything.
export function deriveOptions(rows, filter) {
  const src = filter.from || filter.key;
  if (filter.options) {
    const declared = filter.options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    const hasBlank = (rows || []).some((r) => isBlank(rawValue(r?.[src])));
    return hasBlank ? [...declared, { value: NONE, label: NONE_LABEL }] : declared;
  }
  const seen = new Set();
  let hasBlank = false;
  (rows || []).forEach((r) => {
    const v = rawValue(r?.[src]);
    if (isBlank(v)) { hasBlank = true; return; }
    seen.add(String(v));
  });
  const opts = [...seen].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
  return hasBlank ? [...opts, { value: NONE, label: NONE_LABEL }] : opts;
}

// ═══ NOTHING SELECTED = ALL (Philip, 2026-10-11) ═══
// Every enum chip used to start ON (seeded from the rows), so narrowing a report meant clicking OFF every state but
// the one wanted. Now an enum's selection is the values the user picked, and an EMPTY (or absent) selection means
// "all" — blanks included, so the (None) rule above still holds: nothing hides a blank row unless someone picks
// values that exclude it. Only toggles and searches have declared defaults.
export function initialNonEnumFilterState(definition) {
  const state = {};
  normaliseFilters(definition).forEach((f) => {
    if (f.type === 'toggle') state[f.key] = f.default ?? f.options?.[0]?.value ?? null;
    else if (f.type === 'notices') state[f.key] = [...(f.default || [])];
    else if (f.type === 'note') state[f.key] = '';
    else if (f.type === 'search') state[f.key] = '';
  });
  return state;
}

const enumSel = (state, key) => (Array.isArray(state?.[key]) ? state[key] : []);

// ═══ CASCADING OPTIONS — down the page, in the order the definition declares its filters ═══
// An enum's options are the values present in the rows that pass the enum filters declared ABOVE it (and any
// search): picking a State limits the Property options to that state's properties, and Status (above State) limits
// both. Never upward — if a Property pick narrowed the State chips, choosing a Florida property would hide a Georgia
// state chip the user had lit, and with it every Georgia property still to be picked. A selected value no longer among
// the options (a saved choice, or data that changed) is kept and offered as `unavailable`, so it can be seen and
// removed rather than silently filtering the report to nothing.
const enumsFrom = (definition, key) => {
  const enums = normaliseFilters(definition).filter((f) => f.type === 'enum');
  const i = enums.findIndex((f) => f.key === key);
  return i < 0 ? [] : enums.slice(i);
};

export function facetOptions(rows, definition, state, key) {
  const own = enumsFrom(definition, key);
  if (!own.length) return [];
  const above = { ...state };
  own.forEach((f) => { above[f.key] = []; });   // this filter and everything below it: not applied
  const opts = deriveOptions(applyFilters(rows, definition, above), own[0]);
  const have = new Set(opts.map((o) => o.value));
  const missing = enumSel(state, key).filter((v) => !have.has(v))
    .map((v) => ({ value: v, label: v === NONE ? NONE_LABEL : v, unavailable: true }));
  return [...opts, ...missing];
}

// After the user changes one filter, drop the picks BELOW it that no longer fit (a property in a state just
// deselected). Top to bottom, so each filter is pruned against the already-pruned ones above it. Filters above the
// changed one are never touched.
export function pruneSelections(rows, definition, state, changedKey) {
  let cur = state;
  for (const f of enumsFrom(definition, changedKey).slice(1)) {
    const sel = enumSel(cur, f.key);
    if (!sel.length) continue;
    const avail = new Set(facetOptions(rows, definition, cur, f.key).filter((o) => !o.unavailable).map((o) => o.value));
    const kept = sel.filter((v) => avail.has(v));
    if (kept.length !== sel.length) cur = { ...cur, [f.key]: kept };
  }
  return cur;
}

// ═══ REMEMBERED PER USER (user_column_prefs, the Columns menu's table — table_key 'report-filters:<id>[:<client>]')
// Stored as [{ key, values }] for an enum with picks and [{ key, value }] for a toggle. Searches and years are not
// stored (years open on the app's value year, as before). Reading back ignores anything the report no longer has.
export function filtersToSaved(definition, state) {
  const out = [];
  normaliseFilters(definition).forEach((f) => {
    if (f.type === 'enum' && enumSel(state, f.key).length) out.push({ key: f.key, values: [...enumSel(state, f.key)] });
    else if (f.type === 'toggle' && state?.[f.key] != null) out.push({ key: f.key, value: state[f.key] });
    else if (f.type === 'notices' && enumSel(state, f.key).length) out.push({ key: f.key, values: [...enumSel(state, f.key)] });
    else if (f.type === 'note' && String(state?.[f.key] || '').trim()) out.push({ key: f.key, value: String(state[f.key]) });
  });
  return out;
}

export function filtersFromSaved(definition, saved) {
  const state = initialNonEnumFilterState(definition);
  const byKey = new Map((Array.isArray(saved) ? saved : []).filter((e) => e && e.key).map((e) => [e.key, e]));
  normaliseFilters(definition).forEach((f) => {
    const e = byKey.get(f.key);
    if (!e) return;
    if (f.type === 'enum' && Array.isArray(e.values)) state[f.key] = e.values.map(String);
    else if (f.type === 'toggle' && (f.options || []).some((o) => o.value === e.value)) state[f.key] = e.value;
    else if (f.type === 'notices' && Array.isArray(e.values) && e.values.length) state[f.key] = e.values.map(String);
    else if (f.type === 'note' && typeof e.value === 'string') state[f.key] = e.value;
  });
  return state;
}

// ── filtering ─────────────────────────────────────────────────────────────────────────────────
// Client-side only. `client` and `years` push into SQL via fetch(ctx) and are not re-applied here.
export function applyFilters(rows, definition, state) {
  const filters = normaliseFilters(definition).filter((f) => f.type === 'enum' || f.type === 'toggle' || f.type === 'search');
  if (!filters.length) return rows || [];
  return (rows || []).filter((row) => filters.every((f) => {
    const sel = state?.[f.key];
    if (f.type === 'enum') {
      if (!Array.isArray(sel) || !sel.length) return true;   // nothing selected = all
      return sel.includes(filterKeyOf(rawValue(row?.[f.from || f.key])));
    }
    if (f.type === 'toggle') return true;   // a toggle picks a display mode, it does not exclude rows
    if (f.type === 'search') {
      const q = String(sel || '').trim().toLowerCase();
      if (!q) return true;
      const keys = f.keys || [f.key];
      return keys.some((k) => String(rawValue(row?.[k]) ?? '').toLowerCase().includes(q));
    }
    return true;
  }));
}

export function sortRows(rows, definition, sort) {
  const s = sort || definition?.defaultSort;
  if (!s?.key) return rows || [];
  const col = (definition?.columns || []).find((c) => c.key === s.key);
  return [...(rows || [])].sort(compareBy(s.key, col?.type, s.dir || 'asc'));
}

// ── columns ───────────────────────────────────────────────────────────────────────────────────
// (Philip, 2026-10-10) EVERY column can be hidden except a `locked: true` one — the columns that identify a row
// (Year and Account # on the parcel-year reports). Hidden by default: `optional: true` on the column, or the
// column's key in the report's `defaultHidden` (a shared column hidden on one report only — Level on the
// Executive Summary). A user's own choices are saved per user per report and override these (ReportView).
export const defaultHiddenColumns = (definition) => (definition?.columns || [])
  .filter((c) => !c.locked && (c.optional || (definition.defaultHidden || []).includes(c.key))).map((c) => c.key);

// Hidden keys after the user's saved choices ([{ key, visible }], only the columns they switched) are applied
// over the defaults. A saved choice for a locked column, or a column the report no longer has, is ignored.
export function resolveHidden(definition, saved) {
  const h = new Set(defaultHiddenColumns(definition));
  const byKey = new Map((definition?.columns || []).map((c) => [c.key, c]));
  (saved || []).forEach((e) => {
    const c = byKey.get(e?.key);
    if (!c || c.locked) return;
    if (e.visible === false) h.add(c.key); else h.delete(c.key);
  });
  return [...h];
}

export function visibleColumns(definition, hidden) {
  const h = new Set(hidden || []);
  return (definition?.columns || []).filter((c) => c.locked || !h.has(c.key));
}

// Columns for the EXPORT, which is not always the screen's set.
// ⛔ `exportDefault: true` on an optional column means "the workbook carries this even though the
// screen hides it" — the analyst's spreadsheet has Value Reduction and EffectiveTaxRate as standard
// columns while the screen stays narrow. It applies ONLY while the user has not touched that
// column: once they explicitly hide it, the export follows the screen, because an export that
// re-adds a column someone just removed is the export ignoring them. Definition order is preserved.
export function exportColumns(definition, hidden, touched) {
  const h = new Set(hidden || []);
  const t = new Set(touched || []);
  return (definition?.columns || []).filter((c) => {
    if (c.locked || !h.has(c.key)) return true;     // visible on screen
    return !!c.exportDefault && !t.has(c.key);      // hidden by default, not by the user
  });
}

// A showWhenUsed column appears only when some row has a value in it — or, when it names another column, in that one
// (the Full Tax column rides on Pro-rata %: shown with it, filled on every row). Screen and exports alike.
export const columnUsed = (rows) => (c) => !c.showWhenUsed
  || (rows || []).some((r) => r && r[c.showWhenUsed === true ? c.key : c.showWhenUsed]);

// ── totals ────────────────────────────────────────────────────────────────────────────────────
// ⛔ ON THE FILTERED ROWS. Same rule as the summary block: a footer that sums rows the table is not
// showing is wrong in front of a client.
export function computeTotals(rows, columns) {
  const cols = (columns || []).filter((c) => c.total);
  if (!cols.length) return null;
  const totals = {};
  let any = false;
  cols.forEach((c) => {
    const v = sumColumn(rows, c.key);
    if (v != null) { totals[c.key] = v; any = true; }
  });
  // Pending rows are left out of the sums; the note says how many (e.g. "3 of 5 accounts decided, 2 pending").
  const pendingKey = cols.find((c) => (rows || []).some((r) => rawValue(r?.[c.key]) === PENDING));
  const note = pendingKey ? decidedNote(rows, pendingKey.key) : null;
  if (note) totals.__note = note;
  return any ? totals : null;
}

// ── export ────────────────────────────────────────────────────────────────────────────────────
// ⛔ ONE PAYLOAD, BOTH FORMATS. CSV and XLSX must never diverge — a user comparing the two files
// finding different rows would have no way to know which is right. Whatever is on screen (filtered
// rows, current column selection and order, totals, summary, footnote) is what both carry.
export function buildExportPayload({ definition, rows, columns, totals, summary, ctx }) {
  // `total` travels with each column: a definition's exportSheets computes per-sheet totals from these (the
  // Executive Summary's year tabs). Dropping it here left every multi-year workbook's year tabs with no TOTAL row.
  // ⛔ A showWhenUsed column (the Note column — Baseline year / Non-NTRG) only when a row has a value there. Otherwise
  // every cell is blank and the column reads as missing data (Philip, 2026-10-14; 2026-10-22).
  const anyBaseline = (rows || []).some((r) => r && r.baseline);
  const cols = (columns || []).filter(columnUsed(rows))
    .map((c) => ({ key: c.key, label: c.label, type: c.type, ...(c.total ? { total: true } : {}), ...(c.showWhenUsed ? { showWhenUsed: c.showWhenUsed } : {}) }));
  const footnotes = [];

  // ⛔ Only when a VISIBLE row actually carries a flag. A footnote about a marker that appears
  // nowhere is noise, and it teaches readers to ignore it when it does matter.
  // The tile summary is NOT repeated here — it duplicates the totals row the sheet already has.
  if (anyEstimated(rows, cols)) footnotes.push(ESTIMATE_FOOTNOTE);
  if (anyRecovered(rows, cols)) footnotes.push(RECOVERED_FOOTNOTE);
  if (anyEffective(rows, cols)) footnotes.push(EFFECTIVE_FOOTNOTE);
  if (anyNotMeaningful(rows, cols)) footnotes.push(NOT_MEANINGFUL_FOOTNOTE);
  if ((rows || []).some((r) => r && r.projected) && cols.some((c) => c.key === 'savings')) footnotes.push(PROJECTED_FOOTNOTE);
  // ⛔ Row-level, not cell-level — see BASELINE_FOOTNOTE. Only when a visible row is actually a
  // baseline year, on the same rule as the two above: a note about a marker that appears nowhere
  // teaches readers to ignore it when it does matter.
  if (anyBaseline) {
    footnotes.push(cols.some((c) => c.key === 'engagement') ? BASELINE_FOOTNOTE : BASELINE_UNMARKED_FOOTNOTE);
  }

  // ⛔ SHEETS, not one sheet. A definition may declare exportSheets() and shape the workbook
  // itself — one tab per year, a Summary tab, whatever matches the document it replaces. Without
  // it, one Detail sheet. XLSX gets every sheet; CSV has no concept of one, so it gets the detail
  // only and the UI says so — a silently dropped summary is the kind of thing nobody notices until
  // a client asks where it went.
  const detail = {
    name: definition?.title || 'Report', columns: cols, rows: rows || [],
    totals: totals && cols.length ? { ...totals, [cols[0].key]: totalLabel(totals) } : undefined, footnotes,
  };
  let sheets = null;
  if (definition?.exportSheets) {
    try {
      const built = definition.exportSheets({ rows: rows || [], columns: cols, totals, footnotes, ctx: ctx || {} });
      if (Array.isArray(built) && built.length) sheets = built;
    } catch (e) {
      console.error('[reports] exportSheets threw — exporting a single detail sheet:', e);
    }
  }
  return { sheets: sheets || [detail], columns: cols, rows: rows || [], totals: totals || undefined, footnotes, sheetName: detail.name, summary };
}

// yyyy-mm-dd from LOCAL time — never toISOString(), which rolls the date west of Greenwich.
export function exportStamp(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export const exportFilename = (definition, ext, now) =>
  `${String(definition?.id || 'report')}-${exportStamp(now)}.${ext}`;
